<template>
  <base-page-template>
    <template #toolbar-items>
      <v-toolbar-title class="with-divider">
        {{ title }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <template v-if="tradeContract.status === 'trading'">
        <v-btn color="primary"  class="mr-3" :to="tradeResultLocation" target="_blank">
          <v-icon class="mr-2">icon-trade-result</v-icon>
          取引実績
          <v-icon class="ml-2" small>mdi-open-in-new</v-icon>
        </v-btn>
        <v-btn color="red darken-2 white--text" @click="doSuspend">
          <v-icon class="mr-2">mdi-stop-circle-outline</v-icon>
          取引終了
        </v-btn>
      </template>
      <template v-if="tradeContract.amiIssuer">
        <v-btn color="red darken-2 white--text" @click="doSuspend" v-if="tradeContract.status === 'public'">
          <v-icon class="mr-2">mdi-stop-circle-outline</v-icon>
          募集停止
        </v-btn>
        <v-btn color="primary" @click="doRecreate" v-if="tradeContract.status === 'suspended'">
          <v-icon class="mr-2">mdi-play-circle-outline</v-icon>
          取引再開
        </v-btn>
        <v-btn color="primary" @click="doResume" v-if="tradeContract.status === 'private'">
          <v-icon class="mr-2">mdi-play-circle-outline</v-icon>
          募集再開
        </v-btn>
      </template>
      <template v-else>
        <v-btn color="primary" @click="createSubscription" v-if="tradeContract.status === 'public'">
          <span class="mr-2">申込</span>
          <v-icon>mdi-rss</v-icon>
        </v-btn>
      </template>
    </template>

    <v-container fluid>
      <trade-contract-view-panel
          :trade-contracts="tradeContracts"
          :activate="!loading"
          :hide-self-data-profile="isAssetsTradeContract"
          :hide-self-service-profile="isAssetsTradeContract"
          :hide-other-data-profile="!isAssetsTradeContract">
      </trade-contract-view-panel>
    </v-container>
  </base-page-template>
</template>

<script>
import BasePageTemplate from "@/components/templates/BasePageTemplate.vue";
import {createTradeContractView, emptyTradeContractView} from "@/components/pages/contract/TradeContract/TradeContractView";
import TradeContractViewPanel from "@/components/pages/contract/TradeContract/TradeContractViewPanel.vue";
import {safeMeta} from "@/router/Meta";
import IoablePageMixin from "@/router/IoablePageMixin";
import {mapGetters, mapMutations} from "vuex";
import Swal from "sweetalert2";
import TradeContractUtils from "@/components/pages/contract/TradeContract/TradeContractUtils";
import {loadAllContractProfiles} from "@/components/pages/contract/TradeContract/TradeContractBaseView";
export default {
  name: "ViewContract",
  components: {TradeContractViewPanel, BasePageTemplate},
  mixins: [IoablePageMixin, TradeContractUtils],
  props: {
    tradeContractId: {
      type: String,
      required: true
    }
  },
  data() {
    const first = emptyTradeContractView();
    return {
      loading: true,
      tradeContract: first,
      tradeContracts: [first],
    };
  },
  computed: {
    ...mapGetters({
      hcsClient: 'hcs/client',
    }),
    title() {
      const label = safeMeta(this.$route).label;
      if(this.loading) {
        return label;
      } else {
        return `${label} - ${this.tradeContract.name}`;
      }
    },
    /** 戻るボタンの戻り先 */
    backButtonTo() {
      return this.backToLocation(this.tradeContract);
    },
    tradeResultLocation() {
      return {
        name: '/edit/list/tradeResult/TradeResultShow',
        params: {
          tradeContractId: this.tradeContract.id
        }
      };
    },
    isAssetsTradeContract() {
      return safeMeta(this.$route).isAssetsTradeContract ?? false;
    }
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    async load() {
      // const apiPath = this.isAssetsTradeContract ?
      //     `/assets/trade_contracts/${this.tradeContractId}` :
      //     `/trade_contracts/${this.tradeContractId}`;
      // const {data: tradeContract} = await this.hcsClient.get(apiPath);
      // this.tradeContract = createTradeContractView(tradeContract, this.$store);
      // this.loading = false;

      const apiPath = this.isAssetsTradeContract ?
          `/assets/trade_contracts/${this.tradeContractId}/descendants` :
          `/trade_contracts/${this.tradeContractId}/descendants`;
      const {data: tradeContracts} = await this.hcsClient.get(apiPath);
      this.tradeContracts = tradeContracts.map(tc => createTradeContractView(tc, this.$store));
      this.tradeContract = this.tradeContracts[0];
      loadAllContractProfiles(this.tradeContracts);
      this.loading = false;
    },
    async doSuspend() {
      const {value: confirm} = await Swal.fire({
        title: '確認',
        icon: 'info',
        html: `本当に取引契約 <strong>${this.tradeContract.name}</strong> を終了してよろしいですか。`,
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: true,
        confirmButtonText: "はい",
        cancelButtonText: "いいえ"
      });
      if(!confirm) {
        return;
      }

      const loading = this.$loading({
        lock: true,
        text: `取引契約 ${this.tradeContract.name} を終了しています...`,
        spinner: 'el-icon-loading',
      });
      try {
        const newTc = await this.tradeContract.transitTradeContract('suspend');
        this.showSnackbar({
          message: `取引契約 ${this.tradeContract.name} を終了しました`
        });
        await this.$router.push(this.backToLocation(newTc));
      } catch (e) {
        console.error(e);
        this.showSnackbar({
          color: 'failed',
          message: `取引契約 ${this.tradeContract.name} の終了に失敗しました`
        });
      } finally {
        loading.close();
      }
    },
    async doResume() {
      const {value: confirm} = await Swal.fire({
        title: '確認',
        icon: 'info',
        html: `本当に取引契約 <strong>${this.tradeContract.name}</strong> を再開してよろしいですか。`,
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: true,
        confirmButtonText: "はい",
        cancelButtonText: "いいえ"
      });
      if(!confirm) {
        return;
      }

      const loading = this.$loading({
        lock: true,
        text: `取引契約 ${this.tradeContract.name} を再開しています...`,
        spinner: 'el-icon-loading',
      });
      try {
        const newTc = await this.tradeContract.transitTradeContract('resume');
        this.showSnackbar({
          message: `取引契約 ${this.tradeContract.name} を再開しました`
        });
        await this.$router.push(this.backToLocation(newTc));
      } catch (e) {
        console.error(e);
        this.showSnackbar({
          color: 'failed',
          message: `取引契約 ${this.tradeContract.name} の再開に失敗しました`
        });
      } finally {
        loading.close();
      }
    },
    async doRecreate() {
      const {value: confirm} = await Swal.fire({
        title: '確認',
        icon: 'info',
        html: `本当に取引契約 <strong>${this.tradeContract.name}</strong> を再開してよろしいですか。`,
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: true,
        confirmButtonText: "はい",
        cancelButtonText: "いいえ"
      });
      if(!confirm) {
        return;
      }

      const loading = this.$loading({
        lock: true,
        text: `取引契約 ${this.tradeContract.name} を再開しています...`,
        spinner: 'el-icon-loading',
      });
      try {
        const newTc = await this.tradeContract.duplicate();
        this.showSnackbar({
          message: `取引契約 ${this.tradeContract.name} を再開しました`
        });
        await this.$router.push(this.editLocation(newTc));
      } catch (e) {
        console.error(e);
        this.showSnackbar({
          color: 'failed',
          message: `取引契約 ${this.tradeContract.name} の再開に失敗しました`
        });
      } finally {
        loading.close();
      }
    },
    async createSubscription() {
      const baseTradeContract = this.tradeContract;
      const {value: confirm} = await Swal.fire({
        title: '確認',
        icon: 'info',
        html: `本当に取引契約 <strong>${this.tradeContract.name}</strong> に申込を行ってよろしいですか。`,
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: true,
        confirmButtonText: "はい",
        cancelButtonText: "いいえ"
      });
      if(!confirm) {
        return;
      }

      this.showSnackbar({
        message: `契約 ${baseTradeContract.name} への申込を作成中...`,
        timeout: false,
        loading: true
      });

      try {
        // 取引契約の作成
        const {data: result} = await this.hcsClient.post(`/assets/trade_contracts/${baseTradeContract.id}/pre_subscribe`);
        this.showSnackbar({
          message: `契約 ${baseTradeContract.name} への申込を作成しました`,
        });

        // 取引起案画面に遷移
        await this.$router.push({
          name: '/subscribeContract',
          params: {
            tradeContractId: result.id
          }
        });
      } catch (e) {
        console.error(e);
        this.showSnackbar({
          color: 'failed',
          message: `契約 ${baseTradeContract.name} への申込の作成に失敗しました`,
        });
      }
    }
  }
}
</script>

<style scoped>

</style>
